// src/components/YoloCam.tsx
import React, { useEffect, useRef, useState } from "react";
import {
    IonPage, IonHeader, IonToolbar, IonTitle,
    IonContent, IonButtons, IonButton, IonText,
} from "@ionic/react";
import * as ort from "onnxruntime-web";

declare global {
    interface Navigator { gpu?: unknown } // WebGPU hint
}

type DiePick = {
    value: number; // 1..6
    prob: number;  // 0..1
    box: [number, number, number, number];
};

type Props = {
    modelUrl?: string;
    modelSize?: number;
    labels?: string[];
    confThresh?: number;
    iouThresh?: number;
    minBox?: number;
    /** optional callback each frame with up to 6 best dice */
    onTopDice?: (dice: DiePick[]) => void;
};

const DEFAULT_LABELS = ["Een", "Twee", "Drie", "Vier", "Vijf", "Zes"];
const OUTPUT_LAYOUT = {
    classOffset: 4,
    classProbeCount: 3,
    dims2d: 2,
    dims3d: 3,
} as const;
const BOX_INDICES = {
    centerX: 0,
    centerY: 1,
    width: 2,
    height: 3,
} as const;
const IOU_EPSILON = 1e-6;
const LABEL_DISPLAY = {
    percentageMultiplier: 100,
    textPadding: 6,
    minTop: 18,
    labelHeight: 18,
    textOffsetX: 3,
    textOffsetY: 5,
} as const;
const DEBUG_OVERLAY = {
    rectX: 16,
    rectY: 16,
    rectWidth: 120,
    rectHeight: 80,
    crosshairHalfSize: 20,
    testLabelX: 22,
    testLabelY: 42,
} as const;
const IMAGE_TENSOR = {
    channelCount: 3,
    rgbaStep: 4,
    colorNormalizer: 255,
} as const;
const MAX_REPORTED_DICE = 6;

const YoloDiceCam: React.FC<Props> = ({
                                          modelUrl = "/models/best.onnx",
                                          modelSize = 640,
                                          labels = DEFAULT_LABELS,
                                          confThresh = 0.25,
                                          iouThresh = 0.45,
                                          minBox = 2,
                                          onTopDice,
                                      }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const bgCanvasRef = useRef<HTMLCanvasElement | null>(null);     // background (video/model)
    const overlayRef  = useRef<HTMLCanvasElement | null>(null);     // overlay (boxes/labels)
    const streamRef = useRef<MediaStream | null>(null);
    const rafRef = useRef<number | null>(null);

    const [session, setSession] = useState<ort.InferenceSession | null>(null);
    const [running, setRunning] = useState(false);
    const [status, setStatus] = useState("idle");
    const [topDice, setTopDice] = useState<DiePick[]>([]);

    // --- helpers ---------------------------------------------------------------
    const sigmoid = (x: number) => 1 / (1 + Math.exp(-x));
    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

    function letterbox(ctx: CanvasRenderingContext2D, video: HTMLVideoElement, size: number) {
        const iw = video.videoWidth;
        const ih = video.videoHeight;
        const scale = Math.min(size / iw, size / ih);
        const nw = Math.round(iw * scale);
        const nh = Math.round(ih * scale);
        const dx = Math.floor((size - nw) / 2);
        const dy = Math.floor((size - nh) / 2);
        ctx.clearRect(0, 0, size, size);
        ctx.fillStyle = "black";
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(video, dx, dy, nw, nh);
        return { dx, dy, scale, nw, nh };
    }

    function nms(boxes: number[][], scores: number[], iouT: number, confT: number) {
        const idxs = scores
            .map((s, i) => [s, i] as const)
            .filter(([s]) => s >= confT)
            .sort((a, b) => b[0] - a[0])
            .map(([, i]) => i);

        const keep: number[] = [];
        while (idxs.length) {
            const i = idxs.shift()!;
            keep.push(i);
            const [x1, y1, x2, y2] = boxes[i];
            const areaA = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
            for (let k = idxs.length - 1; k >= 0; k--) {
                const j = idxs[k];
                const [xx1, yy1, xx2, yy2] = boxes[j];
                const inter =
                    Math.max(0, Math.min(x2, xx2) - Math.max(x1, xx1)) *
                    Math.max(0, Math.min(y2, yy2) - Math.max(y1, yy1));
                const areaB = Math.max(0, xx2 - xx1) * Math.max(0, yy2 - yy1);
                const iou = inter / (areaA + areaB - inter + IOU_EPSILON);
                if (iou > iouT) idxs.splice(k, 1);
            }
        }
        return keep;
    }

    // Parse YOLOv8-style: [N, 4+nc] or [1,N,4+nc] or [1,4+nc,N]
    function postprocess(output: ort.Tensor, size: number) {
        const data = output.data as Float32Array;
        let N = 0;
        let stride = 0;
        let at: (row: number, col: number) => number;

        if (output.dims.length === OUTPUT_LAYOUT.dims2d) {
            N = output.dims[0];
            stride = output.dims[1];
            at = (r, c) => data[r * stride + c];
        } else if (output.dims.length === OUTPUT_LAYOUT.dims3d) {
            if (output.dims[0] !== 1) throw new Error(`Unexpected batch ${output.dims[0]}`);
            const d1 = output.dims[1];
            const d2 = output.dims[2];
            if (d1 > d2) {
                N = d1;
                stride = d2;
                at = (r, c) => data[r * stride + c];
            } else {
                N = d2;
                stride = d1;
                at = (r, c) => data[c * N + r];
            }
        } else {
            throw new Error(`Unexpected output dims: ${output.dims.join("x")}`);
        }

        const nc = stride - OUTPUT_LAYOUT.classOffset;
        const boxes: number[][] = [];
        const scores: number[] = [];
        const classes: number[] = [];

        // Decide if logits need sigmoid (classes)
        let needSigmoid = false;
        for (let c = 0; c < Math.min(nc, OUTPUT_LAYOUT.classProbeCount); c++) {
            const v = at(0, OUTPUT_LAYOUT.classOffset + c);
            if (v < 0 || v > 1) { needSigmoid = true; break; }
        }

        for (let i = 0; i < N; i++) {
            const cx = at(i, BOX_INDICES.centerX);
            const cy = at(i, BOX_INDICES.centerY);
            const w = at(i, BOX_INDICES.width);
            const h = at(i, BOX_INDICES.height);
            let x1 = cx - w / 2;
            let y1 = cy - h / 2;
            let x2 = cx + w / 2;
            let y2 = cy + h / 2;
            x1 = clamp(x1, 0, size);
            y1 = clamp(y1, 0, size);
            x2 = clamp(x2, 0, size);
            y2 = clamp(y2, 0, size);
            const bw = x2 - x1;
            const bh = y2 - y1;
            if (bw < minBox || bh < minBox) continue;

            let best = -Infinity;
            let bestIdx = 0;
            for (let c = 0; c < nc; c++) {
                let s = at(i, OUTPUT_LAYOUT.classOffset + c);
                if (needSigmoid) s = sigmoid(s);
                if (s > best) { best = s; bestIdx = c; }
            }
            boxes.push([x1, y1, x2, y2]);
            scores.push(best);
            classes.push(bestIdx);
        }

        const keep = nms(boxes, scores, iouThresh, confThresh);
        return keep.map(i => ({
            xyxy: boxes[i] as [number, number, number, number],
            score: scores[i],
            cls: classes[i],
        }));
    }

    function drawDetections(
        ctx: CanvasRenderingContext2D,
        dets: { xyxy: [number, number, number, number]; score: number; cls: number }[],
        highlight: Set<number> // indexes of dets to highlight (top K)
    ) {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, modelSize, modelSize);

        const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
        ctx.lineWidth = Math.max(2, dpr);
        ctx.font = "14px system-ui, -apple-system, sans-serif";
        ctx.textBaseline = "alphabetic";

        dets.forEach((d, i) => {
            const [x1, y1, x2, y2] = d.xyxy;
            ctx.strokeStyle = highlight.has(i) ? "lime" : "red";
            ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);

            const label = `${labels[d.cls] ?? d.cls} ${(d.score * LABEL_DISPLAY.percentageMultiplier).toFixed(1)}%`;
            const tw = ctx.measureText(label).width + LABEL_DISPLAY.textPadding;
            const ty = Math.max(LABEL_DISPLAY.minTop, y1);
            ctx.fillStyle = "rgba(0,0,0,0.6)";
            ctx.fillRect(x1, ty - LABEL_DISPLAY.labelHeight, tw, LABEL_DISPLAY.labelHeight);
            ctx.fillStyle = "white";
            ctx.fillText(label, x1 + LABEL_DISPLAY.textOffsetX, ty - LABEL_DISPLAY.textOffsetY);
        });
        ctx.restore();
    }

    // Static overlay to verify canvas drawing always works
    function drawDebugOverlay(ctx: CanvasRenderingContext2D, size: number) {
        ctx.save();
        ctx.lineWidth = 2;
        ctx.fillStyle = "rgba(255,0,0,0.25)";
        ctx.fillRect(DEBUG_OVERLAY.rectX, DEBUG_OVERLAY.rectY, DEBUG_OVERLAY.rectWidth, DEBUG_OVERLAY.rectHeight);
        ctx.strokeStyle = "red";
        ctx.strokeRect(DEBUG_OVERLAY.rectX, DEBUG_OVERLAY.rectY, DEBUG_OVERLAY.rectWidth, DEBUG_OVERLAY.rectHeight);
        ctx.beginPath();
        ctx.moveTo(size / 2 - DEBUG_OVERLAY.crosshairHalfSize, size / 2);
        ctx.lineTo(size / 2 + DEBUG_OVERLAY.crosshairHalfSize, size / 2);
        ctx.moveTo(size / 2, size / 2 - DEBUG_OVERLAY.crosshairHalfSize);
        ctx.lineTo(size / 2, size / 2 + DEBUG_OVERLAY.crosshairHalfSize);
        ctx.stroke();
        ctx.font = "16px system-ui, -apple-system, sans-serif";
        ctx.fillStyle = "white";
        ctx.fillText("TEST", DEBUG_OVERLAY.testLabelX, DEBUG_OVERLAY.testLabelY);
        ctx.restore();
    }

    // pick top K dice by probability (after NMS)
    function pickTopDice(
        dets: { xyxy: [number, number, number, number]; score: number; cls: number }[],
        k = MAX_REPORTED_DICE,
        minProb = confThresh
    ): { picks: DiePick[]; indices: number[] } {
        const filtered = dets
            .map((d, i) => ({ ...d, i }))
            .filter(d => d.score >= minProb)
            .sort((a, b) => b.score - a.score)
            .slice(0, k);

        const picks: DiePick[] = filtered.map(d => ({
            value: (d.cls ?? 0) + 1,
            prob: d.score,
            box: d.xyxy,
        }));

        const indices = filtered.map(d => d.i);
        return { picks, indices };
    }

    const BUF = 5;
    const bufRef = useRef<DiePick[][]>([]);
    function stableTopDice(newPicks: DiePick[], k = MAX_REPORTED_DICE): DiePick[] {
        bufRef.current.push(newPicks);
        if (bufRef.current.length > BUF) bufRef.current.shift();
        const flat = bufRef.current.flat();

        const byVal = new Map<number, { sum: number; cnt: number }>();
        for (const p of flat) {
            const m = byVal.get(p.value) ?? { sum: 0, cnt: 0 };
            m.sum += p.prob; m.cnt += 1;
            byVal.set(p.value, m);
        }
        const ranked = Array.from(byVal.entries())
            .map(([value, { sum, cnt }]) => ({ value, avg: sum / cnt }))
            .sort((a, b) => b.avg - a.avg)
            .slice(0, k)
            .map(r => r.value);


        const winners = new Set(ranked);
        const current = newPicks.filter(p => winners.has(p.value));
        return current.length ? current.slice(0, k) : newPicks.slice(0, k);
    }

    const start = async () => {
        try {
            setStatus("Camera lief aankijken.....");
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "environment" },
                audio: false,
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.muted = true;
                videoRef.current.playsInline = true;
                await videoRef.current.play();
            }

            setStatus("Model laden.... Geduldig met je flikkerfiets.");
            const preferredEPs = ("gpu" in navigator)
                ? (["webgpu", "webgl", "wasm"] as const)
                : (["webgl", "wasm"] as const);

            let sess: ort.InferenceSession | undefined;
            let lastErr: unknown;
            for (const ep of preferredEPs) {
                try {
                    sess = await ort.InferenceSession.create(modelUrl, {
                        executionProviders: [ep],
                        graphOptimizationLevel: "all",
                    });
                    break;
                } catch (err) { lastErr = err; }
            }
            if (!sess) throw lastErr ?? new Error("Failed to init ONNX session");

            setSession(sess);
            setRunning(true);
            setStatus("Model draait");
        } catch (e: any) {
            setStatus(`error: ${e?.message ?? e}`);
            console.error(e);
        }
    };

    const stop = () => {
        setRunning(false);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
        setStatus("Gestopt");
    };

    useEffect(() => {
        if (!session || !running) return;

        const loop = async () => {
            if (!running || !videoRef.current || !bgCanvasRef.current || !overlayRef.current) return;

            const size = modelSize;
            const bg = bgCanvasRef.current;
            const ctxBg = bg.getContext("2d");
            const ctxOv = overlayRef.current.getContext("2d");
            if (!ctxBg || !ctxOv) return;

            // ensure both canvases are the same backing size
            if (bg.width !== size || bg.height !== size) {
                bg.width = size; bg.height = size;
            }
            if (overlayRef.current.width !== size || overlayRef.current.height !== size) {
                overlayRef.current.width = size; overlayRef.current.height = size;
            }

            // 1) draw video frame into background canvas
            letterbox(ctxBg, videoRef.current, size);

            // (optional) debug overlay on background
            // drawDebugOverlay(ctxBg, size);

            // 2) build tensor from background canvas
            const imgData = ctxBg.getImageData(0, 0, size, size).data;
            const chw = new Float32Array(IMAGE_TENSOR.channelCount * size * size);
            let r = 0;
            let g = size * size;
            let b = 2 * size * size;
            for (let i = 0; i < imgData.length; i += IMAGE_TENSOR.rgbaStep) {
                chw[r++] = imgData[i] / IMAGE_TENSOR.colorNormalizer;
                chw[g++] = imgData[i + 1] / IMAGE_TENSOR.colorNormalizer;
                chw[b++] = imgData[i + 2] / IMAGE_TENSOR.colorNormalizer;
            }
            const tensor = new ort.Tensor("float32", chw, [1, IMAGE_TENSOR.channelCount, size, size]);

            // 3) inference
            const inputName = session.inputNames?.[0] ?? "images";
            let outputs: ort.InferenceSession.OnnxValueMapType;
            try {
                outputs = await session.run({ [inputName]: tensor });
            } catch (err) {
                console.error("ORT run failed:", err);
                rafRef.current = requestAnimationFrame(loop);
                return;
            }

            // 4) postprocess and pick top dice
            const first = outputs[Object.keys(outputs)[0]] as ort.Tensor;
            const dets = postprocess(first, size);

            const { picks, indices } = pickTopDice(dets, MAX_REPORTED_DICE, confThresh);
            const stable = stableTopDice(picks, MAX_REPORTED_DICE);
            setTopDice(stable);
            if (onTopDice) onTopDice(stable);

            // 5) draw all detections; highlight top K
            drawDetections(ctxOv, dets, new Set(indices));

            rafRef.current = requestAnimationFrame(loop);
        };

        rafRef.current = requestAnimationFrame(loop);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        };
    }, [session, running, modelSize, confThresh, onTopDice]);

    useEffect(() => () => stop(), []); // cleanup on unmount

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>YOLO PWA (ONNX)</IonTitle>
                    <IonButtons slot="end">
                        {!running
                            ? <IonButton onClick={start}>Start</IonButton>
                            : <IonButton color="danger" onClick={stop}>Stop</IonButton>}
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="ion-padding">
                <video ref={videoRef} playsInline muted style={{ display: "none" }} />

                {/* Stacked canvases */}
                <div style={{ position: "relative", display: "inline-block" }}>
                    <canvas
                        ref={bgCanvasRef}
                        style={{
                            width: "min(90vw, 420px)",
                            height: "min(90vw, 420px)",
                            border: "1px solid #333",
                            borderRadius: 12,
                            display: "block",
                        }}
                    />
                    <canvas
                        ref={overlayRef}
                        style={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            width: "min(90vw, 420px)",
                            height: "min(90vw, 420px)",
                            pointerEvents: "none",
                            borderRadius: 12,
                        }}
                    />
                </div>

                <div style={{ marginTop: 8, fontFamily: "system-ui, -apple-system, sans-serif" }}>
                    <IonText color="medium">Status: {status}</IonText>
                    <div style={{ marginTop: 8 }}>
                        {topDice.length
                            ? `Score: ${topDice.reduce((sum, d) => sum + d.value, 0)}`
                            : "Geen detectie door dit ali express model"}
                    </div>
                </div>
            </IonContent>
        </IonPage>
    );
};

export default YoloDiceCam;
